import { useLayers } from '@app/contexts/LayerContext'
import { useMap } from '@app/contexts/ModuleMapContexts/RiskMapContext/RiskMapContext'
import type { RiskWorkspace } from '@app/types'
import * as turf from '@turf/turf'
import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '@app/redux/store'
import {
  getCurrentContexts,
  getCurrentElements,
  getCurrentHazards,
  getIndexedLayers,
} from '../utils/Workspaces.utils'
import { useLiveContext } from '@app/modules/Live/contexts/Live.Context'

export const useWorkspaceLoader = () => {
  const { map, setActiveWorkspace, fitToBounds } = useMap()
  const { prepareAndUpdateLayers } = useLayers()
  const { layerData, regions: availableRegions } = useSelector((state: RootState) => state.riskMap)
  const { liveLayers, setActiveLiveLayers } = useLiveContext()

  const { elementsData, scenariosData, contextsData } = useMemo(
    () =>
      layerData
        ? getIndexedLayers(layerData)
        : { elementsData: {}, scenariosData: {}, contextsData: {} },
    [layerData],
  )

  const allRelevantLayers = useMemo(() => {
    return {
      ...getCurrentElements(elementsData),
      ...getCurrentContexts(contextsData),
      ...getCurrentHazards(scenariosData),
    }
  }, [elementsData, scenariosData, contextsData])

  const loadWorkspace = useCallback(
    async (workspace: RiskWorkspace) => {
      if (!workspace || !map) {
        throw new Error('No Workspace selected or map unavailable')
      }

      // create a shallow copy to avoid mutating the original
      const workspaceCopy: RiskWorkspace = {
        ...workspace,
        layers: {
          ...workspace.layers,
        },
      }

      // remove any invalid layers from the loaded workspace
      workspaceCopy.layers.assets = workspaceCopy.layers.assets.flatMap((id) =>
        allRelevantLayers[id] ? [id] : [],
      )
      workspaceCopy.layers.scenarios = workspaceCopy.layers.scenarios.flatMap((id) =>
        allRelevantLayers[id] ? [id] : [],
      )
      workspaceCopy.layers.contexts = workspaceCopy.layers.contexts.flatMap((id) =>
        allRelevantLayers[id] ? [id] : [],
      )

      const layerIds = [
        ...(workspaceCopy.layers.assets ?? []),
        ...(workspaceCopy.layers.scenarios ?? []),
        ...(workspaceCopy.layers.contexts ?? []),
      ]

      const layersToLoad = layerIds.flatMap((id) =>
        allRelevantLayers[id] ? [allRelevantLayers[id]] : [],
      )

      // Resolve live layers upfront so they can be passed to prepareAndUpdateLayers
      // alongside the regular layers (matching what AddLayersDialog does).
      const savedLiveLayerIds = workspaceCopy.layers.live ?? []
      const liveLayersToRestore = savedLiveLayerIds.length
        ? liveLayers.filter((l) => savedLiveLayerIds.includes(l.id))
        : []

      // Resolve the saved region codes against the available regions list so
      // we can pass full RegionOption objects to the layer pipeline. If saved
      // codes don't resolve (e.g. regions list not loaded yet) we end up with
      // an empty array, which clears any active region mask — matching the
      // semantics for a workspace saved without regions.
      const savedRegionCodes = workspaceCopy.regions ?? []
      const regionsToRestore = savedRegionCodes.length
        ? availableRegions.filter((r) => savedRegionCodes.includes(r.region))
        : []
      if (savedRegionCodes.length && !regionsToRestore.length) {
        // eslint-disable-next-line no-console
        console.warn(
          'Workspace has saved regions but none match the available regions list — skipping region restore',
        )
      }

      // Pass regions AND draw areas into the single layer pass so the first
      // visible-asset computation is already filtered — avoids loading the
      // whole element layer then re-filtering once draw areas hit Redux.
      await prepareAndUpdateLayers({
        layers: [...layersToLoad, ...liveLayersToRestore],
        activeHazardParameters: 'all',
        overwriteHazardParameters: true,
        regionMasks: regionsToRestore,
      })

      // Frame the restored regions, mirroring the add-region UX in
      // RegionFilter (combined bbox of all selected regions).
      if (regionsToRestore.length) {
        const bbox = turf.bbox(
          turf.points(
            regionsToRestore.flatMap(({ min_x, min_y, max_x, max_y }) => [
              [max_x, max_y],
              [min_x, min_y],
            ]),
          ),
        )
        fitToBounds({ boundingBox: [bbox[0], bbox[1], bbox[2], bbox[3]], map })
      }

      // Redux state is synced inside prepareAndUpdateLayers (via getDrawAreas);
      // here we just (re)draw the polygon outline on the map.

      if (liveLayersToRestore.length) {
        setActiveLiveLayers(liveLayersToRestore)
      }

      // Must run after prepareAndUpdateLayers — that clears the active
      // workspace when it detects layers have changed.
      setActiveWorkspace(workspaceCopy)

      return workspaceCopy
    },
    [
      map,
      prepareAndUpdateLayers,
      setActiveWorkspace,
      allRelevantLayers,
      availableRegions,
      fitToBounds,
      liveLayers,
      setActiveLiveLayers,
    ],
  )

  return { loadWorkspace }
}
